import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Signpost, Send, Loader2, Ruler, Package, Layers, PenTool, Truck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SectionHeading } from "./SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATERIALS = [
  { value: "vinyl", label: "Vinyl Banner (510gsm)" },
  { value: "mesh", label: "Mesh Banner" },
  { value: "fabric", label: "Fabric Banner" },
  { value: "corflute", label: "Corflute Sign (5mm)" },
  { value: "aluminium", label: "Aluminium Panel (3mm)" },
];

const FINISHING = [
  { value: "none", label: "No finishing" },
  { value: "eyelets", label: "Eyelets" },
  { value: "ropes", label: "Ropes" },
  { value: "kedar", label: "Kedar edging" },
];

const ARTWORK = [
  { value: "supply", label: "I will supply artwork" },
  { value: "design", label: "Design my artwork (+$75)" },
];

const DELIVERY = [
  { value: "pickup", label: "Pick up - Free" },
  { value: "delivery", label: "Delivery (+$25)" },
];

const fmt = (n) => `$${n.toFixed(2)}`;

export const BannerCalculator = () => {
  const [quantity, setQuantity] = useState(1);
  const [width, setWidth] = useState(2000);
  const [height, setHeight] = useState(1000);
  const [material, setMaterial] = useState("vinyl");
  const [finishing, setFinishing] = useState("eyelets");
  const [artwork, setArtwork] = useState("supply");
  const [delivery, setDelivery] = useState("pickup");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const validDims = width >= 300 && height >= 300 && quantity >= 1;

  useEffect(() => {
    if (!validDims) { setQuote(null); return; }
    const t = setTimeout(async () => {
      setQuoting(true);
      try {
        const res = await axios.post(`${API}/quote/banners`, {
          quantity, width_mm: width, height_mm: height, material, finishing, artwork, delivery,
        });
        setQuote(res.data);
      } catch {
        setQuote(null);
      } finally {
        setQuoting(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [quantity, width, height, material, finishing, artwork, delivery]);

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please add your name and email to request this quote.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries/banner`, {
        name: name.trim(),
        email: email.trim(),
        quote: { quantity, width_mm: width, height_mm: height, material, finishing, artwork, delivery },
      });
      toast.success("Quote request sent — we'll confirm it with you shortly.");
      setName(""); setEmail("");
    } catch {
      toast.error("Couldn't send the request. Please call or email us instead.");
    } finally {
      setSending(false);
    }
  };

  const rows = useMemo(() => quote ? [
    { label: `${quantity} x banner${quantity > 1 ? "s" : ""}`, value: fmt(quote.subtotal) },
    ...(quote.artwork_fee > 0 ? [{ label: "Artwork / design", value: fmt(quote.artwork_fee) }] : []),
    ...(quote.delivery_fee > 0 ? [{ label: "Delivery", value: fmt(quote.delivery_fee) }] : []),
  ] : [], [quote, quantity]);

  return (
    <section id="banners" data-testid="banner-section" className="relative py-28 lg:py-36">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          testId="banner-heading"
          eyebrow="Instant price calculator"
          title="Banner & signage quote calculator"
          description="Banners, corflute signs and aluminium panels — priced by size, material and finishing. Set your specs and send the quote straight to us."
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Controls */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="glass rounded-3xl p-8 md:p-10 flex flex-col gap-7"
          >
            <div className="grid grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Package size={15} className="text-signal" />
                  <label className="text-sm text-muted">Quantity</label>
                </div>
                <Input type="number" min={1} max={1000} value={quantity}
                  data-testid="banner-quantity-input"
                  onChange={(e) => setQuantity(Number(e.target.value) || 0)}
                  className="bg-white/5 border-white/10 text-white focus-visible:ring-signal" />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Ruler size={15} className="text-signal" />
                  <label className="text-sm text-muted">Size (min 300mm)</label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input type="number" min={300} max={5000} value={width}
                    data-testid="banner-width-input"
                    onChange={(e) => setWidth(Number(e.target.value) || 0)}
                    className="bg-white/5 border-white/10 text-white focus-visible:ring-signal" placeholder="W mm" />
                  <Input type="number" min={300} max={5000} value={height}
                    data-testid="banner-height-input"
                    onChange={(e) => setHeight(Number(e.target.value) || 0)}
                    className="bg-white/5 border-white/10 text-white focus-visible:ring-signal" placeholder="H mm" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Layers size={15} className="text-signal" />
                  <label className="text-sm text-muted">Material</label>
                </div>
                <Select value={material} onValueChange={setMaterial}>
                  <SelectTrigger data-testid="banner-material-select" className="bg-white/5 border-white/10 text-white focus:ring-signal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-light border-white/10 text-white">
                    {MATERIALS.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`banner-material-${m.value}`}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Layers size={15} className="text-signal" />
                  <label className="text-sm text-muted">Finishing</label>
                </div>
                <Select value={finishing} onValueChange={setFinishing}>
                  <SelectTrigger data-testid="banner-finishing-select" className="bg-white/5 border-white/10 text-white focus:ring-signal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-light border-white/10 text-white">
                    {FINISHING.map((f) => <SelectItem key={f.value} value={f.value} data-testid={`banner-finishing-${f.value}`}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <PenTool size={15} className="text-signal" />
                  <label className="text-sm text-muted">Artwork</label>
                </div>
                <Select value={artwork} onValueChange={setArtwork}>
                  <SelectTrigger data-testid="banner-artwork-select" className="bg-white/5 border-white/10 text-white focus:ring-signal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-light border-white/10 text-white">
                    {ARTWORK.map((a) => <SelectItem key={a.value} value={a.value} data-testid={`banner-artwork-${a.value}`}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Truck size={15} className="text-signal" />
                  <label className="text-sm text-muted">Delivery / Pick up</label>
                </div>
                <Select value={delivery} onValueChange={setDelivery}>
                  <SelectTrigger data-testid="banner-delivery-select" className="bg-white/5 border-white/10 text-white focus:ring-signal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-light border-white/10 text-white">
                    {DELIVERY.map((d) => <SelectItem key={d.value} value={d.value} data-testid={`banner-delivery-${d.value}`}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>

          {/* Quote panel */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="glass rounded-3xl p-8 md:p-10 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-signal/10 border border-signal/30 text-signal">
                <Signpost size={20} />
              </span>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">Your instant quote</p>
                <p className="font-display text-white font-semibold">
                  {validDims ? `${width} x ${height} mm · ${MATERIALS.find(m => m.value === material).label}` : "Set your banner specs"}
                </p>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 flex-1">
              {quote ? (
                <>
                  {rows.map((r) => (
                    <div key={r.label} className="flex justify-between text-sm">
                      <span className="text-muted">{r.label}</span>
                      <span className="text-white font-medium">{r.value}</span>
                    </div>
                  ))}
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-end justify-between">
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">Total (ex GST)</p>
                      <p className="font-display text-4xl font-bold text-white" data-testid="banner-total">
                        {quoting ? "..." : fmt(quote.total)}
                      </p>
                    </div>
                    <p className="text-muted text-sm text-right" data-testid="banner-per-banner">
                      {fmt(quote.per_banner)} / banner<br />
                      <span className="text-xs text-muted/70">{fmt(quote.total_inc_gst)} incl. GST</span>
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-muted text-sm">Enter a valid size (min 300mm) and quantity to see your price.</p>
              )}
            </div>

            <form onSubmit={handleRequest} data-testid="banner-request-form" className="mt-8 pt-6 border-t border-white/10 flex flex-col gap-4">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-signal">// Request this quote</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                  data-testid="banner-name-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-muted/60 focus-visible:ring-signal" required />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
                  data-testid="banner-email-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-muted/60 focus-visible:ring-signal" required />
              </div>
              <Button type="submit" disabled={sending || !quote}
                data-testid="banner-request-button"
                className="rounded-full bg-signal hover:bg-signal-hover text-white font-semibold py-5 neon-glow transition-colors duration-200">
                {sending
                  ? <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Sending...</span>
                  : <span className="inline-flex items-center gap-2"><Send size={15} /> Send quote request</span>}
              </Button>
              <p className="text-xs text-muted">Indicative pricing in AUD ex GST, based on industry rates plus our margin — we confirm your exact quote before any work starts. Minimum $45 per banner/sign.</p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
